import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import {
  AccessType,
  Case,
  CaseIncidentSummary,
  CasesRequest,
  CaseWithIncidents,
  GetCasesByLocationsRequest,
  HasItems,
  Pagination,
  ShareCasesRequest,
  ShareLogEntry,
} from '../types';

@Injectable({
  providedIn: 'root',
})
export class CaseTrackingService {
  private apiHost = `${environment.apiBaseUrl}`;
  private httpClient = inject(HttpClient);

  mergeCases(request: CasesRequest) {
    const requestUrl = `${this.apiHost}/Cases/Merge`;
    return this.httpClient.post<unknown>(requestUrl, request);
  }

  getCasesByLocations(request: GetCasesByLocationsRequest): Observable<Pagination<CaseWithIncidents>> {
    let params = new HttpParams();
    params = params.append('mode', request.mode);
    request.location_ids.forEach((id) => {
      params = params.append('location_ids', id.toString());
    });
    request.branch_ids.forEach((id) => {
      params = params.append('branch_ids', id.toString());
    });
    request.location_group_ids.forEach((id) => {
      params = params.append('location_group_ids', id.toString());
    });
    if (request.incident_date_from) params = params.append('incident_date_from', request.incident_date_from.toISOString());
    if (request.incident_date_to) params = params.append('incident_date_to', request.incident_date_to.toISOString());

    return this.httpClient.get<Pagination<CaseWithIncidents>>(`${this.apiHost}/Cases`, { params });
  }

  updateCaseName(caseId: string, name?: string): Observable<Case> {
    return this.httpClient.put<Case>(`${this.apiHost}/Cases/${caseId}/name`, {
      name: name ?? null,
    });
  }

  resolveCase(caseId: string): Observable<Case> {
    return this.httpClient.patch<Case>(`${this.apiHost}/Cases/${caseId}/Resolve`, {});
  }

  unresolveCase(caseId: string): Observable<Case> {
    return this.httpClient.patch<Case>(`${this.apiHost}/Cases/${caseId}/Unresolve`, {});
  }

  archiveCase(caseId: string): Observable<Case> {
    return this.httpClient.patch<Case>(`${this.apiHost}/Cases/${caseId}/Archive`, {});
  }

  restoreCase(caseId: string): Observable<Case> {
    return this.httpClient.patch<Case>(`${this.apiHost}/Cases/${caseId}/Restore`, {});
  }

  // the backend actually handles one or either, it doesn't combine emails + to_guests
  shareCases(request: ShareCasesRequest): Observable<void> {
    return this.httpClient.post<void>(`${this.apiHost}/Cases/Share`, request);
  }

  getShareHistories(request: GetCasesByLocationsRequest): Observable<Record<string, ShareLogEntry[]>> {
    let params = new HttpParams();
    params = params.append('mode', request.mode);
    request.location_ids.forEach((id) => {
      params = params.append('location_ids', id.toString());
    });
    request.branch_ids.forEach((id) => {
      params = params.append('branch_ids', id.toString());
    });
    request.location_group_ids.forEach((id) => {
      params = params.append('location_group_ids', id.toString());
    });
    if (request.incident_date_from) params = params.append('incident_date_from', request.incident_date_from.toISOString());
    if (request.incident_date_to) params = params.append('incident_date_to', request.incident_date_to.toISOString());
    return this.httpClient.get<Record<string, ShareLogEntry[]>>(`${this.apiHost}/Cases/Share`, { params });
  }

  getShareHistory(caseId: string): Observable<HasItems<ShareLogEntry>> {
    return this.httpClient.get<HasItems<ShareLogEntry>>(`${this.apiHost}/Cases/${caseId}/Share`);
  }

  getCase(caseId: string): Observable<Case> {
    return this.httpClient.get<Case>(`${this.apiHost}/Cases/${caseId}`);
  }

  trackActivity(reviewId: string, accessType: AccessType, activity?: string): Observable<void> {
    const payload: TrackActivityRequestBody = { review_id: reviewId, access_type: accessType };
    if (activity !== undefined) payload.activity = activity;
    return this.httpClient.post<void>(`${this.apiHost}/Cases/TrackActivity`, payload);
  }

  // example: { "some_id": true } means incident with id 'some_id' should be hidden
  updateIncidentsVisibility(caseId: string, incidentsHiddenValues: Record<string, boolean>): Observable<void> {
    return this.httpClient.patch<void>(`${this.apiHost}/Cases/${caseId}/Visibility`, {
      incidents_hidden_values: incidentsHiddenValues,
    });
  }

  archiveReviewOutcome(caseId: string, reviewOutcomeId: string): Observable<CaseIncidentSummary> {
    return this.httpClient.patch<CaseIncidentSummary>(`${this.apiHost}/Cases/${caseId}/ReviewOutcomes/${reviewOutcomeId}/Archive`, {});
  }

  restoreReviewOutcome(caseId: string, reviewOutcomeId: string): Observable<CaseIncidentSummary> {
    return this.httpClient.patch<CaseIncidentSummary>(`${this.apiHost}/Cases/${caseId}/ReviewOutcomes/${reviewOutcomeId}/Restore`, {});
  }
}

type TrackActivityRequestBody = {
  review_id: string;
  access_type: AccessType;
  activity?: string;
};
